import { SwimlaneDiagram, SwimlaneStep, SwimlaneConnection, SwimlaneLane, LANE_COLORS } from '../types';

/**
 * Конвертор сценариев:
 * - Текстовое описание → SwimlaneDiagram (через шаблон)
 * - Валидация формата
 * - Нормализация данных
 */

/** Стандартные зоны */
const DEFAULT_LANES: SwimlaneLane[] = [
  { id: 'user', label: 'Пользователь', color: LANE_COLORS.user, order: 0 },
  { id: 'app', label: 'МП / Web', color: LANE_COLORS.app, order: 1 },
  { id: 'system', label: 'Система', color: LANE_COLORS.system, order: 2 },
  { id: 'partner', label: 'Партнёр', color: LANE_COLORS.partner, order: 3 },
];

/** Создать пустой шаблон сценария */
export function createEmptyScenario(id: string, title: string, color: string): SwimlaneDiagram {
  return {
    id,
    title,
    description: '',
    color,
    lanes: DEFAULT_LANES.slice(0, 3), // user, app, system
    steps: [
      { id: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s1`, laneId: 'user', label: 'Начало', type: 'start', order: 1 },
      { id: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s2`, laneId: 'app', label: 'Действие', type: 'action', order: 2 },
      { id: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s3`, laneId: 'app', label: 'Конец', type: 'end', order: 3 },
    ],
    connections: [
      { from: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s1`, to: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s2`, type: 'normal' },
      { from: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s2`, to: `${id.toLowerCase().replace(/[^a-z0-9]/g, '')}-s3`, type: 'normal' },
    ],
  };
}

/** Валидация сценария */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateScenario(diagram: SwimlaneDiagram): ValidationResult {
  const errors: string[] = [];

  if (!diagram.id) errors.push('Отсутствует ID сценария');
  if (!diagram.title) errors.push('Отсутствует название');
  if (!diagram.lanes || diagram.lanes.length === 0) errors.push('Нет зон ответственности');
  if (!diagram.steps || diagram.steps.length === 0) errors.push('Нет шагов');

  // Check lane IDs
  const laneIds = new Set(diagram.lanes?.map(l => l.id) || []);
  const stepIds = new Set<string>();

  for (const step of (diagram.steps || [])) {
    if (stepIds.has(step.id)) {
      errors.push(`Дублирующийся ID шага: ${step.id}`);
    }
    stepIds.add(step.id);

    if (!laneIds.has(step.laneId)) {
      errors.push(`Шаг "${step.id}" ссылается на несуществующую зону "${step.laneId}"`);
    }
  }

  // Check connections
  for (const conn of (diagram.connections || [])) {
    if (!stepIds.has(conn.from)) {
      errors.push(`Связь из несуществующего шага "${conn.from}"`);
    }
    if (!stepIds.has(conn.to)) {
      errors.push(`Связь к несуществующему шагу "${conn.to}"`);
    }
  }

  // Check start/end
  const hasStart = diagram.steps?.some(s => s.type === 'start');
  const hasEnd = diagram.steps?.some(s => s.type === 'end');
  if (!hasStart) errors.push('Нет начального шага (start)');
  if (!hasEnd) errors.push('Нет конечного шага (end)');

  return { valid: errors.length === 0, errors };
}

/** Нормализация: добавить недостающие поля, пронумеровать */
export function normalizeScenario(diagram: SwimlaneDiagram): SwimlaneDiagram {
  const lanes = (diagram.lanes || []).map((lane, i) => ({
    ...lane,
    color: lane.color || LANE_COLORS[lane.id] || '#f5f5f5',
    order: lane.order ?? i,
  }));

  const steps: SwimlaneStep[] = (diagram.steps || []).map((step, i) => ({
    ...step,
    type: step.type || 'action',
    order: step.order ?? (i + 1),
  }));

  const connections: SwimlaneConnection[] = (diagram.connections || []).map(conn => ({
    ...conn,
    type: conn.type || 'normal',
  }));

  return {
    ...diagram,
    lanes,
    steps,
    connections,
  };
}

/**
 * Промпт для Qwen 2.5 / ИИ конвертации текста → JSON.
 * Используется когда оператор вставляет текстовое описание процесса.
 */
export const AI_CONVERSION_PROMPT = `Преобразуй следующее текстовое описание бизнес-процесса в JSON-формат swimlane-диаграммы.

Формат ответа (только JSON, без пояснений):
{
  "id": "SC-NNN",
  "title": "Название",
  "description": "Описание",
  "color": "#hex",
  "lanes": [{"id": "user|app|system|partner", "label": "...", "color": "#hex", "order": N}],
  "steps": [{"id": "scNNN-sN", "laneId": "...", "label": "до 25 символов", "description": "полное описание", "type": "start|end|action|decision|switch|subprocess|data|io|error", "order": N}],
  "connections": [{"from": "scNNN-sN", "to": "scNNN-sM", "label": "метка", "type": "normal|error|conditional"}]
}

Правила:
1. Зоны: user (Пользователь), app (МП/Web), system (Система), partner (Партнёр)
2. Первый шаг — type: start, последний — type: end
3. Условия — type: decision (ромб), множественные условия — type: switch (шестиугольник)
4. Внешние вызовы (SumSub, Verestro) — в зону partner
5. Ошибки — type: error с connections type: error
6. Ветвления — connections type: conditional с label "Да"/"Нет"

Описание процесса:
`;
